// src/components/StatsigReadyGuardPolling.tsx
import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';
interface StatsigReadyGuardPollingProps {
  /** The content to render once Statsig is ready or timeout occurs. */
  children: ReactNode;
  /** Maximum time in milliseconds to wait for Statsig initialization. Defaults to 3000ms (3 seconds). */
  maxWaitTimeMs?: number;
  /** Content to display while waiting (e.g., a loading spinner). Defaults to null (renders nothing while waiting). */
  fallback?: ReactNode;
  /** Interval in milliseconds for checking the loading state. Defaults to 1000ms (1 second). */
  pollingIntervalMs?: number;
}

/**
 * A wrapper component that delays rendering its children until the Statsig SDK
 * is initialized or a maximum wait time has passed, checking periodically.
 * NOTE: Prefer the non-polling version unless polling is strictly required,
 * as it's generally less efficient.
 */
const StatsigReadyGuardPolling: React.FC<StatsigReadyGuardPollingProps> = ({
  children,
  maxWaitTimeMs = 3000,
  fallback = null,
  pollingIntervalMs = 1000 // Check every 1 second by default
}) => {
  const { client } = useStatsigClient();
  const [isReadyToRender, setIsReadyToRender] = useState<boolean>(false);
  const [_, setHasTimedOut] = useState<boolean>(false); // Track timeout explicitly

  // Use a ref to store the latest isLoading value.
  // This allows the interval callback to access the *current* state
  // without making the main useEffect dependent on `isLoading`.
  const isLoadingRef = useRef(client.loadingStatus === 'Loading' || client.loadingStatus === 'Uninitialized');
  useEffect(() => {
    isLoadingRef.current = client.loadingStatus === 'Loading' || client.loadingStatus === 'Uninitialized';
  }, [client.loadingStatus]); // Update the ref whenever isLoading changes

  useEffect(() => {
    // --- Exit conditions ---
    // 1. If we've already decided to render, do nothing.
    if (isReadyToRender) {
      return;
    }
    // 2. If Statsig is *already* initialized when this effect runs.
    //    We check the ref here as it holds the most up-to-date value synced by the other effect.
    if (!isLoadingRef.current) {
      console.log('StatsigReadyGuard (Polling): SDK already initialized on mount/effect run.');
      setIsReadyToRender(true);
      return;
    }

    // --- Setup polling and timeout ---
    console.log(
      `StatsigReadyGuard (Polling): SDK not ready. Starting checks every ${pollingIntervalMs}ms. Max wait: ${maxWaitTimeMs}ms.`
    );

    let intervalId: NodeJS.Timeout | undefined;
    let timeoutId: NodeJS.Timeout | undefined;

    // Function to check status and update state if needed
    const checkStatus = () => {
      if (!isLoadingRef.current) {
        console.log('StatsigReadyGuard (Polling): SDK initialized (detected by check).');
        setIsReadyToRender(true);
        // Clean up immediately when goal is met
        if (intervalId) clearInterval(intervalId);
        if (timeoutId) clearTimeout(timeoutId);
        return true; // Indicate success
      }
      console.log('StatsigReadyGuard (Polling): Still loading...');
      return false; // Indicate still loading
    };

    // Start polling interval
    intervalId = setInterval(checkStatus, pollingIntervalMs);

    // Set overall timeout
    timeoutId = setTimeout(() => {
      console.warn(`StatsigReadyGuard (Polling): Max wait time (${maxWaitTimeMs}ms) reached.`);
      // Perform one final check *just in case* it loaded between the last interval and the timeout
      if (!checkStatus()) {
        // If still not loaded after final check, force render due to timeout
        setHasTimedOut(true); // Mark as timed out (optional state for debugging/logging)
        setIsReadyToRender(true); // Render children anyway
      }
      // Clean up interval if timeout occurs (checkStatus might have already cleared it if loaded just in time)
      if (intervalId) clearInterval(intervalId);
    }, maxWaitTimeMs);

    // --- Cleanup function ---
    // This runs when the component unmounts or dependencies change
    return () => {
      console.log('StatsigReadyGuard (Polling): Cleaning up interval and timeout.');
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };

    // --- Dependencies ---
    // Rerun effect only if maxWaitTimeMs or pollingIntervalMs changes,
    // or after we've decided to render (to trigger cleanup via the return function).
    // We specifically DO NOT include `isLoading` here because we are using the ref
    // and setInterval to handle the check, avoiding re-running the whole setup on every loading state flicker.
  }, [maxWaitTimeMs, pollingIntervalMs, isReadyToRender]); // hasTimedOut is implicitly covered by isReadyToRender

  // Render fallback or children based on the state
  return isReadyToRender ? <>{children}</> : <>{fallback}</>;
};

export default StatsigReadyGuardPolling;
